/**
 * Scoring rules seen two ways (web.md §8 fix 7): the Topic editor's Scoring block shows the rules whose
 * condition is exactly one `topic-score(this topic) ≥ N` with one action; Models → "Actions on scoring"
 * shows every scoring rule in order. Both edit the same `rules` rows.
 *
 * Positions: every per-topic row sits ABOVE the seeded ladder, and within a topic a higher N sits above
 * a lower one. The seeded ladder (six rows from data schema 3: a block per model) is core's
 * `SCORING_LADDER_SEED`, so "Reset ladder" restores exactly what a fresh workspace has.
 */

import { SCORING_LADDER_SEED, type GroupNode, type ModelId, type Rule, type RuleAction, type RuleId, type ScoringRule, type TopicId } from '@digester/core';

export interface TopicRow {
  ruleId: RuleId;
  topicId: TopicId;
  min: number;
  action: RuleAction;
  /** The model whose score the row tests (models.md §8.1); null = the shown scores. */
  model: string | null;
}

/** The (topic, N, action) of a per-topic row, or null when the rule has another shape. */
export function topicRowOf(rule: Rule): TopicRow | null {
  if (rule.kind !== 'scoring' || rule.archivedAt !== null || rule.actions.length !== 1) return null;
  const root = rule.condition;
  if (root.children.length !== 1) return null;
  let node = root.children[0];
  if (node?.type === 'group') {
    if (node.children.length !== 1) return null;
    node = node.children[0];
  }
  if (!node || node.type !== 'condition' || node.field !== 'topic-score' || node.operator !== 'gte' || !node.topic) return null;
  const action = rule.actions[0];
  if (!action) return null;
  return { ruleId: rule.id, topicId: node.topic, min: node.value, action, model: node.model ?? null };
}

/** Live per-topic rows of one topic, highest N first. */
export function rowsOfTopic(rules: readonly Rule[], topicId: string): TopicRow[] {
  return rules
    .map(topicRowOf)
    .filter((r): r is TopicRow => r !== null && r.topicId === topicId)
    .sort((a, b) => b.min - a.min);
}

/** The condition of a per-topic row; `model` = the model whose score it tests (null = the shown scores). */
export function topicCondition(topicId: TopicId, min: number, model: string | null = null): GroupNode {
  return {
    type: 'group',
    operator: 'all',
    children: [{ type: 'condition', field: 'topic-score', operator: 'gte', value: min as never, topic: topicId, ...(model ? { model: model as ModelId } : {}) }],
  };
}

/** The seeded ladder's ids (core `SCORING_LADDER_SEED`; "Reset ladder" is `engine.rules.resetLadder`). */
const LADDER_IDS: ReadonlySet<string> = new Set(SCORING_LADDER_SEED.map((r) => r.id));

export function isLadderRule(id: string): boolean {
  return LADDER_IDS.has(id);
}

/**
 * The evaluation order after a topic's rows changed: other rules keep their relative order; the topic's
 * rows form one block, highest N first, placed where the topic's first row was — but never below the
 * first ladder rule (then directly above the ladder).
 */
export function orderWithTopicBlock(live: readonly ScoringRule[], topicId: string, block: readonly RuleId[]): RuleId[] {
  const blockSet = new Set<string>(block);
  const firstOld = live.findIndex((r) => {
    const row = topicRowOf(r);
    return row !== null && row.topicId === topicId;
  });
  const others = live.filter((r) => !blockSet.has(r.id) && (topicRowOf(r)?.topicId ?? null) !== topicId).map((r) => r.id);
  const firstLadder = others.findIndex((id) => isLadderRule(id));
  const ladderAt = firstLadder < 0 ? others.length : firstLadder;
  let at = ladderAt;
  if (firstOld >= 0) {
    // Index of the old block's start among the remaining rules.
    const before = live.slice(0, firstOld).filter((r) => !blockSet.has(r.id) && (topicRowOf(r)?.topicId ?? null) !== topicId).length;
    at = Math.min(before, ladderAt);
  }
  return [...others.slice(0, at), ...block, ...others.slice(at)];
}

/**
 * Where a NEW scoring rule goes (found in M6's own use: appended at the end it sat below the ladder's
 * "below the bar" rows, `best-score[model] ≥ 0`, and could never match). It goes directly above the first
 * seeded ladder row that tests a model it names — so an on-device rule stays below Jev's block and a
 * Cyrillic message still waits for Jev — or above the first ladder row when it names none of their models.
 * Without a live ladder row it stays where it is (last).
 */
export function orderWithNewRule(live: readonly ScoringRule[], id: RuleId, models: readonly string[]): RuleId[] {
  const others = live.filter((r) => r.id !== id);
  const ladder = others.filter((r) => isLadderRule(r.id));
  const ofModel = ladder.find((r) => modelsOf(r).some((m) => models.includes(m)));
  const target = ofModel ?? ladder[0];
  const ids = others.map((r) => r.id);
  if (!target) return [...ids, id];
  const at = ids.indexOf(target.id);
  return [...ids.slice(0, at), id, ...ids.slice(at)];
}

function modelsOf(rule: ScoringRule): string[] {
  const out: string[] = [];
  const visit = (n: GroupNode | GroupNode['children'][number]): void => {
    if (n.type === 'group') n.children.forEach(visit);
    else if ((n.field === 'best-score' || n.field === 'topic-score') && n.model) out.push(n.model);
  };
  visit(rule.condition);
  return out;
}

export function sameOrder(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/** A row's display name in Models ("AI ≥ 90 → Priority"). */
export function topicRowName(topicName: string, min: number, actionText: string): string {
  return `${topicName} ≥ ${min} → ${actionText}`.slice(0, 80);
}
